import { Location } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { CardOverviewContent, PrismicCardOverview } from "../../../../types/app-types";
import { FilterService } from '../../../components/filter/filter.service';
import { FunctieCardEnum } from "../../../components/functie/card/functie-card.enum";
import { convertToHttpParams } from '../../../helpers/HttpParamsConverter';
import { Functie } from '../../../model';
import { MatchTestResult, Profiel, ProfileMatchView } from '../../../model/profiel/profiel';
import { CompareService } from '../../../services/compare/compare.service';
import { ContentService } from '../../../services/content/content.service';
import { PrismicService } from '../../../services/prismic/prismic.service';
import { ProfileStorageService } from '../../../services/profile/profile-storage.service';
import { FunctieResource } from '../../../services/resources/functie.resource';
import { ProfielResource } from '../../../services/resources/profiel.resource';
import { SurveyResource } from "../../../services/resources/survey.resource";
import { ProfileMatchSelectionTypeEnum } from "../../../services/util/profile-match-selection-type.enum";
import { UtilService } from '../../../services/util/util.service';

const content = require('./functie-overzicht-page.content.json');
const defaultPagingOptions = { offset: 0, size: 20 };
export const testResultQueryParamKey = 'test_dialog_id';
export const testSurveyQueryParamKey = 'test_survey_id';
const QUICK_FILTER_PARAM_KEY = 'quickFilter';

@Component({
  selector: 'app-lijst',
  templateUrl: './functie-overzicht-page.component.html',
  styleUrls: ['./functie-overzicht-page.component.scss']
})
export class FunctieOverzichtPageComponent implements OnInit, OnDestroy {
  public functies: Functie[] = [];
  public total: number = 0;
  public loading: boolean = true;
  public currentSort: string = 'rel';
  public itemsToCompare: any[] = [];
  public query: string = '';
  public isMobile: boolean = false;
  public filterState: any = {};
  public initFilterState: boolean = false;
  public rawFilters: any;
  public filter$: BehaviorSubject<any>;
  public subscriptions: Subscription[] = [];
  public isLoggedIn: boolean = false;
  public showMatchPercentage: boolean = false;
  public showNameSort: boolean = true;
  public showRelevanceSort: boolean = true;
  public testDialogId: number;
  public testSurveyId: number;
  public FunctieCardEnum = FunctieCardEnum;
  public cmsContent: CardOverviewContent;
  public quickFilterParamKey = QUICK_FILTER_PARAM_KEY;

  @ViewChild('compareDialog', { static: true, read: ElementRef })
  public compareDialog: ElementRef;
  public minimizeCompare: boolean = false;
  public previewHits: number;
  public profileMatchSelectionType?: ProfileMatchSelectionTypeEnum;
  public profielMatchSelection?: ProfileMatchView;

  // This is hardcoded for now, because we want to control the names and order of the filters
  public quickFilters = [
    'Begeleiding', 'Behandeling', 'Verpleging', 'Verzorging',
    'Directe ondersteuning', 'Administratie', 'ICT', 'Facilitair',
    'Management', 'Overige staf'
  ];
  public quickFilterMapping: Record<string, string[]> = {
    Begeleiding: [
      'Begeleiding',
      'Begeleiding GGZ',
      'Begeleiding GHZ',
      'Begeleiding thuiszorg',
      'Jeugdzorg',
      'Kinderopvang',
      'Sociaal werk'
    ],
    Behandeling: [
      'Gedragskundig behandelaar',
      'Medisch behandelaar',
      'Paramedisch',
      'Vaktherapie'
    ],
    Verpleging: [
      'Gespecialiseerde verpleging',
      'Verpleging IC',
      'Verpleging acute zorg',
      'Verpleging algemene zorg',
      'Verpleging thuiszorg',
      'Verpleging woonzorgcentrum'
    ],
    Verzorging: [
      'Kraamzorg',
      'Verzorging',
      'Verzorging thuiszorg',
      'Verzorging woonzorgcentrum'
    ],
    'Directe ondersteuning': [
      'Anesthesie',
      'Apotheek',
      'Diagnostiek ondersteuning',
      'Laboratorium',
      'Medische beeldvorming / radiotherapie',
      'Medische ondersteuning',
      'Medische techniek',
      'Sterilisatie'
    ],
    Administratie: [
      'Administratie',
      'Financiën',
      'Planning',
      'Post, repro en archief',
      'Secretarieel'
    ],
    ICT: [
      'ICT'
    ],
    Facilitair: [
      'Groenvoorziening',
      'Maaltijdvoorziening',
      'Receptie en beveiliging',
      'Schoonmaak',
      'Techniek',
      'Transport en logistiek',
      'Winkelvoorziening'
    ],
    Management: [
      'Management'
    ],
    'Overige staf': [
      'Arbo, kwaliteit en preventie',
      'Beleid',
      'Bemiddeling',
      'HR',
      'Inkoop',
      'Juridisch',
      'Marketing en communicatie',
      'Onderzoek',
      'Opleiding',
      'Projectleiding',
      'Verkoop'
    ]
  };

  public selectedQuickFilter: string | null = null;

  public selectQuickFilter(filter: string): void {
    if (this.selectedQuickFilter === filter) {
      // deselect
      this.selectedQuickFilter = null;
      delete this.filterState.functiefamilie;
    } else {
      this.selectedQuickFilter = filter;
      this.filterState.functiefamilie = [...this.quickFilterMapping[filter]];
    }
    // reset paging to first page
    this.handleFilterStateChange(this.filterState, { offset: 0, size: defaultPagingOptions.size });
  }

  public constructor(
    private functieResource: FunctieResource,
    private route: ActivatedRoute,
    private matDialog: MatDialog,
    private compareService: CompareService,
    private renderer: Renderer2,
    private utilService: UtilService,
    private contentService: ContentService,
    private profileStorageService: ProfileStorageService,
    private profielResource: ProfielResource,
    private location: Location,
    private prismic: PrismicService,
    private filterService: FilterService,
    private surveyResource: SurveyResource,
    private router: Router
  ) {
    this.contentService.loadContent(content);
    this.subscriptions.push(
      this.utilService.getScreenMode$().subscribe(screenMode => {
        this.isMobile = screenMode.includes('MOBILE');
      })
    );
    this.prismic.getDocumentByUid<PrismicCardOverview>('card_overview', 'functie_overzicht').then(doc => {
      this.cmsContent = this.transform(doc.data);
    });

    this.subscriptions.push(this.filterService.previewHits.subscribe(filterState => {
      const salaris = filterState.find(x => x?.key === 'salaris')?.value;
      filterState = filterState.filter(x => x.key !== 'salaris');
      const params: any = {};
      filterState.forEach(filter => {
        if (params[filter.key]) {
          params[filter.key].push(filter.value);
        } else {
          params[filter.key] = [filter.value];
        }
      });
      if (salaris) {
        params.salaris = salaris + '-';
      }
      this.functieResource.zoekFuncties({
        size: 0,
        offset: 0,
        ...params
      }).subscribe(resp => {
        this.previewHits = resp.total;
        this.filterService.previewTotal.next(this.previewHits);
      });
    }));
  }

  public ngOnInit(): void {
    this.renderer.setStyle(this.compareDialog.nativeElement, 'display', 'none');
    this.compareService.getItemsToCompare$().subscribe((itemsToCompare: any[]) => {
      this.itemsToCompare = itemsToCompare;
      const displayStyle = this.itemsToCompare.length > 0 ? 'block' : 'none';
      this.renderer.setStyle(this.compareDialog.nativeElement, 'display', displayStyle);
      this.minimizeCompare = false;
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public get hasActiveFilters(): boolean {
    return Object
      .values(this.filterState)
      .some(v => Array.isArray(v) && v.length > 0);
  }

  private convertQueryParamsToFilterOptions(params: Params): void {
    this.initFilterState = true;
    const quick = params[QUICK_FILTER_PARAM_KEY];
    if (quick && this.quickFilterMapping[quick]) {
      this.selectedQuickFilter = quick;
    } else {
      this.selectedQuickFilter = null;
    }
    const ignoreQueryParams = ['offset', testResultQueryParamKey, testSurveyQueryParamKey, QUICK_FILTER_PARAM_KEY];
    const newFilterState: any = {};
    const newPagingOptions = { ...defaultPagingOptions };
    Object.keys(params).forEach(key => {
      const paramValue = params[key];
      if (ignoreQueryParams.includes(key)) {
        return;
      } else if (key === 'size') {
        newPagingOptions[key] = Number(paramValue);
      } else if (key === 'sort') {
        this.currentSort = paramValue;
      } else {
        if (key === 'query' || this.rawFilters.find(filter => filter.zoekVeld === key)) {
          if (key === 'salaris') {
            newFilterState[key] = this.convertQueryParamToRangeFilters(paramValue);
          } else {
            newFilterState[key] = Array.isArray(paramValue) ? paramValue : [paramValue];
          }
        }
      }
    });
    this.handleFilterStateChange(newFilterState, newPagingOptions);
  }

  private convertQueryParamToRangeFilters(value: string): any[] {
    if (value.endsWith('-')) {
      return [value.slice(0, -1)];
    } else {
      return [];
    }
  }

  public getMore(): void {
    if (this.loading || this.functies.length === this.total) {
      return;
    }
    this.loading = true;
    this.filter$.next(
      Object.assign({}, this.filter$.value, {
        offset: this.filter$.value.offset + this.filter$.value.size
      })
    );
  }

  public sort(string: string): void {
    this.currentSort = string;
    this.filter$.next(
      Object.assign({}, this.filter$.value, {
        sort: string,
        offset: 0
      })
    );
  }

  public handleFilterStateChange($event: any, pagingOptions: any = defaultPagingOptions): void {
    this.filterState = { ...$event };
    const requestParams = cloneDeep($event);
    if (requestParams.salaris) {
      requestParams.salaris[0] = [requestParams.salaris[0] + '-'];
    }
    this.filter$.next(Object.assign({ sort: this.currentSort }, pagingOptions, requestParams));
  }

  public setupFilterPipe(): void {
    this.subscriptions.push(this.filter$
      .pipe(
        tap(filter => {
          if (!filter.functiefamilie || filter.functiefamilie.length === 0) {
            this.selectedQuickFilter = null;
          }
        }),
        tap(filter => !this.initFilterState && this.updateURL(filter)),
        tap(filter => this.checkIfPagingIsReset(filter)),
        map(item => this.checkIfSortIsRelevance(item)),
        this.profileMatchSelectionType && this.profielMatchSelection ?
          //10677
          switchMap(filter => this.functieResource.zoekFunctiesEnMatchMetProfielSelectie(filter, this.profielMatchSelection, this.profileMatchSelectionType))
          :
          switchMap(filter => this.functieResource.zoekFuncties(filter))
      )
      .subscribe((functies: any) => {
        this.initFilterState = false;
        this.concatFuncties(functies);
        this.updateMetaData(functies);
      }));
  }

  private updateURL(filter: any): void {
    let httpParams = convertToHttpParams(filter);
    if (this.testDialogId) httpParams = httpParams.set(testResultQueryParamKey, this.testDialogId);
    if (this.testSurveyId) httpParams = httpParams.set(testSurveyQueryParamKey, this.testSurveyId);
    if (this.selectedQuickFilter) {
      httpParams = httpParams.set(QUICK_FILTER_PARAM_KEY, this.selectedQuickFilter);
    }
    this.location.go('functies', httpParams.toString());
  }

  private checkIfPagingIsReset(filter: any): void {
    this.loading = true;
    if (filter.offset === 0) {
      this.functies = [];
    }
  }

  private checkIfSortIsRelevance(item: any): any {
    if (item.sort === 'rel') {
      delete item.sort;
    }
    return item;
  }

  private concatFuncties(functies): void {
    this.functies = this.functies.concat(
      functies.items.map((functie, index) => {
        return { index, ...functie };
      })
    );
  }

  private updateMetaData(functies: any): void {
    this.total = functies.total;
    this.loading = false;
  }

  public search(query: any): void {
    if (this.filterState.query && this.filterState.query.indexOf(query) === -1) {
      this.filterState.query.push(query);
    } else {
      this.filterState.query = [query];
    }
    this.query = '';
    this.handleFilterStateChange(this.filterState);
  }

  private setupTestResultOnlyFunctionMatchMode(params: Params, testResultsPreferences: MatchTestResult) {
    this.profileMatchSelectionType = ProfileMatchSelectionTypeEnum.AlleenTestResultaatEigenschappen;
    this.profielMatchSelection = new ProfileMatchView({ ...testResultsPreferences } as Profiel);
    this.showMatchPercentage = true;
    this.showRelevanceSort = false;
    this.showNameSort = false;
    this.currentSort = 'match:desc';
    this.setupAndLoadFilterPipe(params);
  }

  private setupFullProfileFunctionMatchMode(params: Params, profile: Profiel) {
    this.profileMatchSelectionType = ProfileMatchSelectionTypeEnum.TotaleProfiel;
    this.profielMatchSelection = new ProfileMatchView(profile);
    this.showMatchPercentage = true;
    this.currentSort = 'match:desc';
    this.setupAndLoadFilterPipe(params);
  }

  private setupNoFunctionMatchMode(params: Params) {
    this.showMatchPercentage = false
    this.setupAndLoadFilterPipe(params);
  }

  private determineFunctionMatchMode(params: Params) {
    if (params[testResultQueryParamKey] && params[testSurveyQueryParamKey]) {
      this.testDialogId = Number(params[testResultQueryParamKey]);
      this.testSurveyId = Number(params[testSurveyQueryParamKey]);
      this.profielResource.getProfileTestResultPreferences([this.testDialogId]).pipe(
        take(1)
      ).subscribe(testResultsPreferences => {
        if (testResultsPreferences && this.profileContainsMatchableData({ ...testResultsPreferences } as Profiel)) {
          this.setupTestResultOnlyFunctionMatchMode(params, testResultsPreferences);
        } else {
          this.setupNoFunctionMatchMode(params)
        }
      })
    } else {
      this.profileStorageService.loggedInSubject.pipe(
        tap(isLoggedIn => this.isLoggedIn = isLoggedIn),
        switchMap(isLoggedIn => {
          if (isLoggedIn) {
            return this.profielResource.getProfielWithCompletedSurveysAndTestResultsPreferences()
              .pipe(catchError(() => of(null)))
          } else {
            return of(null);
          }
        }
        ),
        take(1)
      ).subscribe(profile => {
        if (profile && this.profileContainsMatchableData(profile)) {
          this.setupFullProfileFunctionMatchMode(params, profile)
        } else {
          this.setupNoFunctionMatchMode(params)
        }
      })
    }
  }

  public handleFiltersLoaded($event: any) {
    this.rawFilters = $event;
    this.route.queryParams.subscribe(params => {
      this.determineFunctionMatchMode(params);
    });
  }

  private setupAndLoadFilterPipe(params: Params) {
    this.filter$ = new BehaviorSubject({ ...defaultPagingOptions, sort: this.currentSort });
    this.convertQueryParamsToFilterOptions(params);
    this.setupFilterPipe();
  }

  private profileContainsMatchableData(profiel: Profiel): boolean {
    return (
      profiel.interesses?.length > 0 ||
      profiel.functies?.some(functie => functie.esId) ||
      profiel.opleidingen?.some(functie => functie.esId) ||
      profiel.werkvermogens?.length > 0 ||
      profiel.persoonlijkeEigenschappen?.length > 0
    );
  }

  private transform(doc: any): CardOverviewContent {
    return {
      title: this.prismic.convertToText(doc.overview_title),
      description: this.prismic.convertToHtml(doc.overview_description),
      overviewTitleImage: doc.overview_title_image
    };
  }

  public hideCompareDialog(hide: boolean): void {
    this.minimizeCompare = hide;
  }

  public backToTestResults() {
    this.router.navigate(['test', this.testSurveyId, 'resultaat', this.testDialogId]);
  }
}

